import { create } from 'zustand';

type State = {
  cache: Record<string, ArrayBuffer>;
  actions: {
    set: (attachmentId: string, data: ArrayBuffer) => void;
  };
};

const usePdfBytesStore = create<State>((set) => ({
  cache: {},
  actions: {
    // First write wins. A mounted preview hands its buffer to react-pdf, which transfers it to the
    // PDF.js worker (detaching it); replacing it would give <Document> a new `file`, and its
    // changed-file check reads the detached old one and throws (OLIO-3618). fetchPdfBytes already
    // avoids a second download; this keeps the invariant for any other writer.
    set: (attachmentId, data) =>
      set((state) => (state.cache[attachmentId] ? state : { cache: { ...state.cache, [attachmentId]: data } })),
  },
}));

export const usePdfBytes = (attachmentId: string) => usePdfBytesStore((state) => state.cache[attachmentId]);
export const usePdfBytesActions = () => usePdfBytesStore((state) => state.actions);

// In-flight downloads are tracked outside the store, keyed by attachment, so every caller for an
// attachment shares one download. A re-hover mid-download signs a fresh URL, and fetching it too
// would download the bytes twice (OLIO-3618). A caller with a different URL only falls back to it if
// the shared download fails: signed URLs expire ~30s after issue, and a retry armed with a fresh URL
// must not inherit the failure of a doomed request for an expired one.
const inFlight = new Map<string, { url: string; promise: Promise<ArrayBuffer> }>();

function download(attachmentId: string, url: string): Promise<ArrayBuffer> {
  const promise = (async () => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`status ${response.status}`);
    const buffer = await response.arrayBuffer();
    usePdfBytesStore.getState().actions.set(attachmentId, buffer);
    return buffer;
  })().finally(() => {
    inFlight.delete(attachmentId);
  });
  inFlight.set(attachmentId, { url, promise });
  return promise;
}

export function fetchPdfBytes(attachmentId: string, url: string): Promise<ArrayBuffer> {
  const cached = usePdfBytesStore.getState().cache[attachmentId];
  if (cached) return Promise.resolve(cached);
  const existing = inFlight.get(attachmentId);
  if (!existing) return download(attachmentId, url);
  // Same URL: nothing new to try, so share its outcome, failure included.
  if (existing.url === url) return existing.promise;
  // If several callers fall back at once, the first one's retry is the shared download for the rest.
  return existing.promise.catch(() => inFlight.get(attachmentId)?.promise ?? download(attachmentId, url));
}
